import React, { useCallback, useEffect, useState } from "react";
import { Col, Row, Card, Button, Progress, Flex, Steps, Breadcrumb, Tooltip } from "antd";
import { ReactComponent as Right } from "../assets/icons/Right.svg";
import { ReactComponent as Left } from "../assets/icons/Left.svg";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCourseItemAsync, saveProgressAsync, selectCourse } from "../features/courses/coursesSlice";
import CourseService from "../util/course";
import { UnorderedListOutlined } from "@ant-design/icons";
import { LESSON_TYPE } from "../util/constants";
import { CourseResult, CourseQuiz, CourseEmbed, CourseFile, CourseBasic } from "../components";
import useStickyState from "../hooks/useStickyState";
import useInactivity from "../hooks/useInactivity";
import { useActiveTime } from "../hooks/useActiveTime";
import { CourseComplete } from "../components/CourseComplete";
import { getLessonTime, getAllLessonTimes, updateLessonTime } from "../util/lessonTimeTracker";

export default function CourseLessons({ location }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const data = useSelector((state) => selectCourse(state));
    const status = useSelector((state) => state.courses.status);
    const error = useSelector((state) => state.courses.error);
    const [current, setCurrent] = useState(-1);
    const [quizAnswers, setQuizAnswers] = useState({});
    const [stats, setStats] = useState({});
    const [lessons, setLessons] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [controls, setControls] = useState(null);
    const [showCourseContent, setShowCourseContent] = useStickyState(false, "showCourseMenu");
    const { isInactive, isTabInactive } = useInactivity();
    const [timeSpent, resetTimeSpent] = useActiveTime(isInactive || isTabInactive);

    // Function to update local storage with time spent
    const updateLocalTimeSpent = useCallback(() => {
        if (current < 0 || !lessons[current]) return;
        
        // Only update if timeSpent is significant (at least 5 seconds)
        if (timeSpent < 5) return;
        
        // Get the current lesson
        const currentLesson = lessons[current];
        const lessonId = `${currentLesson.__typename}${currentLesson.id}`;
        
        // Update the local storage with the new time spent
        updateLessonTime(id, lessonId, timeSpent);
        
        // Reset timeSpent after updating localStorage
        resetTimeSpent();
    }, [id, timeSpent, lessons, current, resetTimeSpent]);

    // Function to save progress and activity to the backend
    const saveProgressAndActivity = useCallback(() => {
        if (current < 0 || !lessons[current]) return;
        
        // Update local storage first
        updateLocalTimeSpent();
        
        // Get all lesson times from local storage
        const allLessonTimes = getAllLessonTimes(id);
        
        // Create the activity object from local storage data
        const activity = {};
        Object.keys(allLessonTimes).forEach(key => {
            activity[key] = {
                secondsSpent: allLessonTimes[key]
            };
        });
        
        // Get the current lesson
        const currentLesson = lessons[current];
        const key = `${currentLesson.__typename}${currentLesson.id}`;
        const hasCompleted = data.attributes.progress?.[key]?.completed;
        const totalLessons = data.attributes.course.data.attributes.Lessons.length;
        const isFinalLesson = current === totalLessons - 1;
        
        // Preserve existing progress data
        const existingProgress = data.attributes.progress || {};
        
        // Save progress and activity
        dispatch(
            saveProgressAsync({
                id,
                data: {
                    progress: {
                        ...existingProgress,
                        [key]: {
                            ...existingProgress[key],
                            completed: true,
                        },
                    },
                    activity: activity,
                    isCompleted: isFinalLesson,
                },
            })
        );
    }, [dispatch, id, data, lessons, current, updateLocalTimeSpent]);

    useEffect(() => {
        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, []);

    useEffect(() => {
        if (status === "idle") {
            dispatch(fetchCourseItemAsync(id));
        }
    }, [dispatch, status, data, id]);

    useEffect(() => {
        if (!!(data && data.attributes && data.attributes.course.data.attributes.Lessons)) {
            const result = {
                __typename: LESSON_TYPE.COMPLETE,
                id: "complete",
                title: "Credits",
            }
            setLessons([...data.attributes.course.data.attributes.Lessons, result]);
            
            // Initialize local storage with existing activity data from the backend
            if (data.attributes.activity) {
                Object.keys(data.attributes.activity).forEach(key => {
                    const lessonId = key;
                    const secondsSpent = data.attributes.activity[key].secondsSpent || 0;
                    
                    // Only set if not already in localStorage (to avoid overwriting newer data)
                    if (!localStorage.getItem(`lessonTime_${id}_${lessonId}`)) {
                        localStorage.setItem(`lessonTime_${id}_${lessonId}`, secondsSpent.toString());
                    }
                });
            }
        }
    }, [data, status, id]);

    useEffect(() => {
        const completedLessons =
            Object.keys(data?.attributes?.progress ?? {}).filter((key) => data?.attributes?.progress[key]?.completed).length - 1;
        if (current === -1 && completedLessons >= 0 && lessons.length > 0) {
            setCurrent(completedLessons === lessons.length - 1 ? completedLessons : completedLessons + 1);
        } else if (current === -1 && data?.attributes !== null && lessons.length > 0) {
            setCurrent(0);
        }

        if (lessons[current]?.__typename === LESSON_TYPE.QUIZ && data?.attributes?.progress) {
            const progress = data?.attributes?.progress[`${lessons[current]?.__typename}${lessons[current]?.id}`];
            setQuizAnswers(progress?.progress ?? {});
            setStats(progress?.stats ?? {});
        }
    }, [current, data, lessons, status]);

    // Update local storage when timeSpent changes
    useEffect(() => {
        updateLocalTimeSpent();
    }, [updateLocalTimeSpent, timeSpent]);

    // Save progress and activity when component unmounts
    // useEffect(() => {
    //     return () => {
    //         if (current >= 0 && lessons[current]) {
    //             saveProgressAndActivity();
    //         }
    //     };
    // }, [saveProgressAndActivity, current, lessons]);

    if (status === "pending" || !data) return <div className="AlertInfo Loading">Loading...</div>;
    if (error) return <p>Error :(</p>;

    const onChangeSteps = (value) => {
        // Save progress and activity before changing steps
        saveProgressAndActivity();
        
        setCurrent(value);
    };

    const onNext = async () => {
        // Save progress and activity before moving to next
        saveProgressAndActivity();
        
        const totalLessons = data.attributes.course.data.attributes.Lessons.length;
        const isFinalLesson = current === totalLessons - 1;
        
        if (current === lessons.length - 1) {
            return navigate("/courses");
        }

        setCurrent(current + 1);
    };

    const onPrev = () => {
        if (current === 0) return;
        
        // Save progress and activity before moving to previous
        saveProgressAndActivity();
        
        setCurrent(current - 1);
    };

    // Helper function to format countdown as MM:ss
    const formatCountdown = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const generateButtons = () => {
        const actions = [];
        if (current !== 0) {
            actions.push(
                <Button key="prev-btn" type="primary" size="small" onClick={onPrev}>
                    <Left /> Prev
                </Button>
            );
        }

        if (
            (current !== lessons.length - 1 && lessons[current]?.__typename !== LESSON_TYPE.QUIZ) ||
            (current !== lessons.length - 1 &&
                lessons[current]?.__typename === LESSON_TYPE.QUIZ &&
                data.attributes.progress?.[`${lessons[current].__typename}${lessons[current].id}`]?.completed)
        ) {
            const completedCourse =
                current >= 0 ? data.attributes.progress?.[`${lessons[current].__typename}${lessons[current].id}`]?.completed : false;
            
            const currentLesson = lessons[current];
            const timer = currentLesson?.timer;
            
            // Determine if next button should be disabled
            let isDisabled = true;
            let buttonText = "Next";
            
            // If there's a timer and lesson is not completed, check controls state
            if (timer && !completedCourse) {
                // If controls not set yet or still counting, disable button
                if (!controls || controls.state === "counting") {
                    isDisabled = true;
                    if (controls && controls.countdown !== undefined) {
                        buttonText = `Next (${formatCountdown(controls.countdown)})`;
                    } else {
                        buttonText = "Next (Loading...)";
                    }
                } else if (controls.state === "ended") {
                    // Timer completed, enable button
                    isDisabled = false;
                }
            } else if (controls && !completedCourse) {
                // No timer but controls exist - check if lesson component hasn't finished loading
                if (controls.state === "counting") {
                    isDisabled = true;
                    if (controls.countdown !== undefined) {
                        buttonText = `Next (${formatCountdown(controls.countdown)})`;
                    } else {
                        buttonText = "Next (Loading...)";
                    }
                } else if (controls.state === "ended") {
                    // Lesson ready, enable button
                    isDisabled = false;
                }
            } else {
                // No timer and no controls (or completed), enable button
                isDisabled = false;
                buttonText = "Next";
            }
            
            actions.push(
                <Button
                    key="next-btn"
                    type="primary"
                    size="small"
                    onClick={onNext}
                    disabled={isDisabled}
                >
                    {buttonText} <Right />
                </Button>
            );
        }

        if (data.attributes.isCompleted && current === lessons.length - 1) {
            actions.push(
                <Button key="back-to-courses-btn" type="primary" size="small" onClick={() => navigate("/courses")}>
                    Back to Courses
                </Button>
            );
        }
        return actions;
    };

    const getLessonType = (lesson) => {
        if (lesson.__typename === LESSON_TYPE.QUIZ) {
            return "Quiz";
        } else if (lesson.__typename === LESSON_TYPE.QUESTION) {
            return "Question";
        } else if (lesson.__typename === LESSON_TYPE.EMBED) {
            return "Embed";
        } else if (lesson.__typename === LESSON_TYPE.FILE) {
            return "File";
        } else if (lesson.__typename === LESSON_TYPE.RESULT) {
            return "";
        } else if (lesson.__typename === LESSON_TYPE.COMPLETE) {
            return "";
        } else {
            return "Lesson";
        }
    };
    const getIfLessonDisabled = (index) => {
        const completedLessons = Object.keys(data?.attributes?.progress ?? {}).filter(
            (key) => data?.attributes?.progress[key]?.completed
        )?.length;
        return index > completedLessons;
    };

    return (
        <div className="CourseLessons PageSingle">
            <div className="AppContainer">
                <Row>
                    <Col span={23}>
                        <Breadcrumb
                            className="Breadcrumb"
                            items={[
                                {
                                    title: <Link to="/">Home</Link>,
                                },
                                {
                                    title: <Link to="/courses">My Courses</Link>,
                                },
                                {
                                    title: <Link to={`/courses/${id}`}>{data.attributes.course.data?.attributes?.Title}</Link>,
                                },
                                {
                                    title: "Lessons",
                                },
                            ]}
                        />
                    </Col>
                </Row>
                <Row gutter={[20, 30]}>
                    <Col xs={24} md={18} lg={20}>
                        <Flex justify="space-between" align="center">
                            <h1 className="title">{data.attributes.course.data?.attributes?.Title}</h1>
                        </Flex>
                    </Col>
                    <Col xs={24} md={6} lg={4} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                        <Tooltip placement="bottom" title="Course Content">
                            <Button
                                onClick={() => setShowCourseContent(!showCourseContent)}
                                size="small"
                                className={showCourseContent ? "BtnMd Opened" : "BtnMd"}
                            >
                                <UnorderedListOutlined /> {showCourseContent ? "Hide" : "Show"} Course Content
                            </Button>
                        </Tooltip>
                    </Col>
                    <Col xs={{ span: 24, order: 3 }} md={{ span: showCourseContent ? 16 : 24, order: 4 }}>
                        <Card bordered={false} className="CardItem">
                            <div className="CardQuiz">
                                {lessons[current]?.__typename === LESSON_TYPE.RESULT ? (
                                    <CourseResult />
                                ) : lessons[current]?.__typename === LESSON_TYPE.QUIZ ? (
                                    <CourseQuiz lesson={lessons[current]} onNext={onNext} current={current} />
                                ) : lessons[current]?.__typename === LESSON_TYPE.EMBED ? (
                                    <CourseEmbed lesson={lessons[current]} controls={controls} setControls={setControls} />
                                ) : lessons[current]?.__typename === LESSON_TYPE.FILE ? (
                                    <CourseFile lesson={lessons[current]} controls={controls} setControls={setControls}/>
                                ) : lessons[current]?.__typename === LESSON_TYPE.COMPLETE ? (
                                    <CourseComplete lesson={lessons[current]} course={data}/>
                                ) : (
                                    <CourseBasic lesson={lessons[current]} setControls={setControls} controls={controls}/>
                                )}
                            </div>
                        </Card>
                    </Col>
                    {showCourseContent && (
                        <Col xs={{ span: 24, order: 3 }} md={{ span: 8, order: 5 }}>
                            <Card bordered={false} className="CardItem CardSteps">
                                <Steps
                                    direction="vertical"
                                    onChange={onChangeSteps}
                                    current={current}
                                    items={lessons.map((l, i) => ({
                                        title: l.title,
                                        description: getLessonType(l),
                                        disabled: getIfLessonDisabled(i),
                                    }))}
                                />
                            </Card>
                        </Col>
                    )}
                </Row>
            </div>
            <div className="LessonsMenu">
                <div className="AppContainer">
                    <Row>
                        <Col xs={24} md={12}>
                            <Flex justify="space-between" align="flex-end">
                                <div className="CardProgress">
                                    <Progress percent={CourseService.calculateProgress(data)} />
                                </div>
                            </Flex>
                        </Col>
                        <Col xs={24} md={12} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                            <Flex justify="center" align="center" gap={10}>
                                {generateButtons()}
                            </Flex>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
}
